/** @jsxImportSource @emotion/react */
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import {
  useCampaignDetail,
  campaignKeys,
  getCollection,
  useActiveCampaigns,
  useReserveCampaignTicket,
  useRetryCampaignPayment,
} from "../../hooks/useCampaignQueries";
import CampaignStatusBadge from "./components/CampaignStatusBadge";
import ReservationCountdown from "./components/ReservationCountdown";
import BookedTicketPanel from "./components/BookedTicketPanel";
import CampaignPaymentForm from "./components/CampaignPaymentForm";
import CampaignCard from "./components/CampaignCard";
import { fetchStripeKey } from "../../api/campaignApi";
import {
  CAMPAIGN_STATUS,
  formatDateTime,
  formatCurrency,
  getAvailabilityText,
  getCampaignDescription,
  getCampaignCityName,
  getCampaignCityUuid,
  getCampaignEndDate,
  getCampaignEventDate,
  getCampaignImage,
  getCampaignStatus,
  getCampaignTitle,
  getCampaignUuid,
  getReservationExpiry,
} from "./utils";
import pageStyles from "./pageStyles";
import t from "../../theme";
import Footer1 from "../../components/Footer/Footer";
import AppModal from "../../components/AppModal";

const CampaignDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignUuid } = useParams();
  const selCity = location?.state?.selCity;
  const { userDetails } = useContext(UserContext);
  const queryClient = useQueryClient();
  const token = userDetails?.token;
  const { data, isLoading, isError, refetch } = useCampaignDetail({ campaignUuid, token });
  const { data: activeCampaignsData, isLoading: isTopCampaignsLoading } = useActiveCampaigns({
    token,
    limit: 8,
    city: selCity,
  });
  const reserveMutation = useReserveCampaignTicket({ campaignUuid, token });
  const retryMutation = useRetryCampaignPayment({ campaignUuid, token });
  const [reservedUntil, setReservedUntil] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeError, setStripeError] = useState("");
  const [paymentClientSecret, setPaymentClientSecret] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentMode, setPaymentMode] = useState("payment");
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isPaymentBooked, setIsPaymentBooked] = useState(false);

  const campaign = useMemo(() => {
    const payload = data?.data || data || {};
    const campaignPayload = payload?.campaign || data?.campaign || payload;
    return {
      ...campaignPayload,
      user_ticket:
        payload?.user_ticket ||
        payload?.userTicket ||
        campaignPayload?.user_ticket ||
        campaignPayload?.userTicket,
      booked_ticket:
        payload?.booked_ticket ||
        payload?.bookedTicket ||
        campaignPayload?.booked_ticket ||
        campaignPayload?.bookedTicket,
      ticket:
        payload?.ticket ||
        campaignPayload?.ticket,
      ticket_uuid:
        payload?.ticket_uuid ||
        payload?.ticketUuid ||
        campaignPayload?.ticket_uuid ||
        campaignPayload?.ticketUuid,
      discounted_price:
        payload?.discounted_price ??
        campaignPayload?.discounted_price,
    };
  }, [data]);
  const status = getCampaignStatus(campaign);
  const expiresAt = reservedUntil || getReservationExpiry(campaign);
  const hasActiveReservation =
    expiresAt && !Number.isNaN(new Date(expiresAt).getTime()) && new Date(expiresAt).getTime() > Date.now();
  const displayStatus = isPaymentBooked
    ? CAMPAIGN_STATUS.REDEEMED
    : status === CAMPAIGN_STATUS.REDEEMED
      ? CAMPAIGN_STATUS.REDEEMED
      : hasActiveReservation
      ? CAMPAIGN_STATUS.RESERVED
      : status;
  const endDate = getCampaignEndDate(campaign);
  const eventDate = getCampaignEventDate(campaign);
  const campaignImage = getCampaignImage(campaign);
  const originalPrice = formatCurrency(campaign.original_price);
  const discountedPrice = formatCurrency(campaign.discounted_price);
  const savings =
    Number.isFinite(Number(campaign.original_price)) &&
    Number.isFinite(Number(campaign.discounted_price))
      ? formatCurrency(Number(campaign.original_price) - Number(campaign.discounted_price))
      : "";
  const discountPercent =
    Number.isFinite(Number(campaign.original_price)) &&
    Number(campaign.original_price) > 0 &&
    Number.isFinite(Number(campaign.discounted_price))
      ? Math.round(
          ((Number(campaign.original_price) - Number(campaign.discounted_price)) /
            Number(campaign.original_price)) *
            100,
        )
      : null;
  const isReserved = displayStatus === CAMPAIGN_STATUS.RESERVED;
  const isRedeemed = displayStatus === CAMPAIGN_STATUS.REDEEMED;
  const isExpired = displayStatus === CAMPAIGN_STATUS.EXPIRED;
  // The reserve endpoint only accepts campaigns whose raw backend status is
  // "active" (Campaign.status is draft/active/closed, default "draft") —
  // but CAMPAIGN_STATUS/normalizeStatus has no "not published yet" state, so
  // a draft campaign otherwise displays as perfectly bookable and only fails
  // with a confusing "Campaign not found" once the user clicks Book. Mirror
  // the server's own gate here so the UI is honest about it up front.
  const rawCampaignStatus = String(campaign.status || "").toLowerCase();
  const isNotPublished =
    Boolean(rawCampaignStatus) &&
    rawCampaignStatus !== "active" &&
    !isReserved &&
    !isRedeemed;
  // The reserve endpoint rejects a consumer whose *profile* city differs from
  // the campaign's city (campaigns with no city are open to everyone). The
  // browsing city from the location selector does not lift that rule, so a
  // campaign found by browsing another city can be viewed but not reserved —
  // mirror the server check here instead of letting the user hit a 403.
  const campaignCityUuid = getCampaignCityUuid(campaign);
  const campaignCityName = getCampaignCityName(campaign);
  const profileCityUuid = userDetails?.address?.city || "";
  const isOutsideProfileCity =
    Boolean(campaignCityUuid) && campaignCityUuid !== profileCityUuid;
  const outsideCityMessage = !profileCityUuid
    ? "Add your home city to your profile to book tickets for this campaign."
    : campaignCityName
      ? `Tickets for this campaign can only be booked in ${campaignCityName}, which is not your home city.`
      : "This campaign runs in another city, so tickets can only be booked from your home city.";
  const topCampaigns = useMemo(() => {
    const getNumber = (item, keys) => {
      const value = keys.map((key) => item?.[key]).find((entry) => entry != null && entry !== "");
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    };
    const getBookedCount = (item) =>
      getNumber(item, ["booked_count", "bookings_count", "total_bookings", "bookedTickets"]);
    const getAvailableCount = (item) =>
      getNumber(item, [
        "available",
        "availableTickets",
        "available_tickets_count",
        "available_tickets",
        "remaining_tickets",
        "remaining_count",
      ]);

    return getCollection(activeCampaignsData)
      .filter((item) => getCampaignUuid(item) && getCampaignUuid(item) !== campaignUuid)
      .sort((first, second) => {
        const firstBookings = getBookedCount(first) ?? 0;
        const secondBookings = getBookedCount(second) ?? 0;
        if (secondBookings !== firstBookings) return secondBookings - firstBookings;

        const firstAvailable = getAvailableCount(first) ?? Number.MAX_SAFE_INTEGER;
        const secondAvailable = getAvailableCount(second) ?? Number.MAX_SAFE_INTEGER;

        return firstAvailable - secondAvailable;
      })
      .slice(0, 3);
  }, [activeCampaignsData, campaignUuid]);

  useEffect(() => {
    let isMounted = true;
    fetchStripeKey()
      .then((key) => {
        if (isMounted && key) setStripePromise(loadStripe(key));
      })
      .catch((error) => {
        if (isMounted) setStripeError(error?.message || "Unable to load Stripe.");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // payment-init returns the amount it actually charged; fall back to the
  // campaign's discounted price if an older backend omits it.
  const getPaymentAmount = (payload = {}) => {
    const value = payload?.amount ?? payload?.data?.amount;
    return Number.isFinite(Number(value)) ? Number(value) : null;
  };

  const getClientSecret = (payload = {}) =>
    payload.clientSecret ||
    payload.client_secret ||
    payload?.data?.clientSecret ||
    payload?.data?.client_secret ||
    payload?.paymentIntent?.client_secret;

  // "setup" when the campaign is free: the card is verified, not charged.
  const getPaymentMode = (payload = {}) =>
    payload?.mode || payload?.data?.mode || "payment";

  const startPayment = async () => {
    const paymentResult = await retryMutation.mutateAsync();

    const clientSecret = getClientSecret(paymentResult);
    if (!clientSecret) {
      throw new Error("Payment client secret was not returned by the backend.");
    }
    setPaymentAmount(getPaymentAmount(paymentResult) ?? campaign.discounted_price ?? null);
    setPaymentMode(getPaymentMode(paymentResult));
    setPaymentClientSecret(clientSecret);
    setIsPaymentOpen(true);
    return clientSecret;
  };

  const onReserve = async () => {
    if (isOutsideProfileCity) return;

    try {
      const result = await reserveMutation.mutateAsync();
      const nextExpiresAt = getReservationExpiry(result?.data || result);
      if (nextExpiresAt) setReservedUntil(nextExpiresAt);
      toast.success(
        result?.message ||
          "Ticket held for 2 minutes — complete payment to confirm it.",
      );
      await startPayment();
    } catch (error) {
      toast.error(error?.message || "Unable to hold this ticket.");
    }
  };

  // A payment session already exists, so the modal can be reopened without
  // asking the backend for another PaymentIntent.
  const hasPaymentSession = Boolean(paymentClientSecret && stripePromise);

  const reopenPayment = () => setIsPaymentOpen(true);

  const closePayment = () => {
    // Never tear the form down while Stripe is confirming — the success
    // handler lives in the unmounting component.
    if (isPaymentProcessing) return;
    setIsPaymentOpen(false);
  };

  const onRetryPayment = async () => {
    try {
      await startPayment();
    } catch (error) {
      toast.error(error?.message || "Unable to start payment retry.");
    }
  };

  const onReservationExpire = useCallback(() => {
    setReservedUntil("");
    setPaymentClientSecret("");
    setPaymentAmount(null);
    setIsPaymentOpen(false);
    setIsPaymentBooked(false);
    refetch();
    queryClient.invalidateQueries({ queryKey: ["campaigns", "active"] });
    queryClient.invalidateQueries({ queryKey: ["campaigns", "list"] });
    queryClient.invalidateQueries({ queryKey: campaignKeys.tickets });
  }, [queryClient, refetch]);

  const onPaymentSuccess = useCallback(async () => {
    setPaymentClientSecret("");
    setPaymentAmount(null);
    setIsPaymentOpen(false);
    setReservedUntil("");
    setIsPaymentBooked(true);
    queryClient.invalidateQueries({ queryKey: ["campaigns", "active"] });
    queryClient.invalidateQueries({ queryKey: ["campaigns", "list"] });
    queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignUuid) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.tickets });

    // The ticket only flips from "reserved" to "booked" once Stripe's webhook
    // lands, which can trail the client-side payment confirmation by a few
    // seconds. Poll briefly so /my-bookings doesn't land while the ticket is
    // still mid-confirmation and show "not ready for download yet".
    const POLL_INTERVAL_MS = 1200;
    const POLL_TIMEOUT_MS = 10000;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let ticketReady = false;
    while (Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      const result = await refetch();
      const payload = result?.data?.data || result?.data || {};
      const campaignPayload = payload?.campaign || payload;
      const ticketStatus =
        payload?.user_ticket?.status ||
        payload?.userTicket?.status ||
        campaignPayload?.user_ticket?.status ||
        campaignPayload?.userTicket?.status;
      if (ticketStatus === "booked" || ticketStatus === "used") {
        ticketReady = true;
        break;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    if (!ticketReady) {
      toast.info("Payment received — confirming your ticket, this can take a few seconds.");
    }

    navigate("/my-bookings");
  }, [campaignUuid, navigate, queryClient, refetch]);

  return (
    <div css={pageStyles.route}>
      <main css={pageStyles.page}>
        <div css={pageStyles.container}>
          <header css={pageStyles.header}>
            <div>
              <p css={pageStyles.eyebrow}>Campaign Detail</p>
            </div>
            <Button
              css={pageStyles.backButton}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => navigate("/campaigns", { state: { selCity } })}
            >
              Campaigns
            </Button>
          </header>

          {isLoading && (
            <div css={pageStyles.state}>
              <CircularProgress size={26} />
            </div>
          )}

          {isError && <div css={pageStyles.state}>Unable to load campaign details.</div>}

          {!isLoading && !isError && (
            <div css={pageStyles.detailShell}>
              <div css={pageStyles.detailLayout}>
            <section css={[pageStyles.card, isRedeemed && pageStyles.bookedDetailCard]}>
              {campaignImage ? (
                <img
                  css={pageStyles.detailImage}
                  src={campaignImage}
                  alt={getCampaignTitle(campaign)}
                />
              ) : (
                <div css={[pageStyles.detailImage, pageStyles.detailImageFallback]}>
                  <CampaignOutlinedIcon />
                  <span>Campaign</span>
                </div>
              )}
              {isRedeemed && (
                <BookedTicketPanel
                  campaign={campaign}
                  token={token}
                />
              )}
              {!isRedeemed && (
                <>
                  <div css={pageStyles.statusRow}>
                    <CampaignStatusBadge status={displayStatus} />
                    {isReserved && expiresAt && (
                      <ReservationCountdown
                        expiresAt={expiresAt}
                        onExpire={onReservationExpire}
                      />
                    )}
                  </div>
                  <h2 css={pageStyles.detailTitle}>{getCampaignTitle(campaign)}</h2>
                  <div
                    css={pageStyles.detailText}
                    dangerouslySetInnerHTML={{
                      __html: getCampaignDescription(campaign),
                    }}
                  />
                </>
              )}
              <div css={pageStyles.metaGrid}>
                {eventDate && (
                  <div css={pageStyles.metaCard}>
                    <span css={pageStyles.metaLabel}>
                      <EventOutlinedIcon css={pageStyles.metaIcon} />
                      Event Date
                    </span>
                    <span css={pageStyles.metaValue}>{formatDateTime(eventDate)}</span>
                  </div>
                )}
                {campaign.venue && (
                  <div css={pageStyles.metaCard}>
                    <span css={pageStyles.metaLabel}>
                      <PlaceOutlinedIcon css={pageStyles.metaIcon} />
                      Venue
                    </span>
                    <span css={pageStyles.metaValue}>{campaign.venue}</span>
                  </div>
                )}
                <div css={pageStyles.metaCard}>
                  <span css={pageStyles.metaLabel}>
                    <ConfirmationNumberIcon css={pageStyles.metaIcon} />
                    Availability
                  </span>
                  <span css={pageStyles.metaValue}>{getAvailabilityText(campaign)}</span>
                </div>
                <div css={pageStyles.metaCard}>
                  <span css={pageStyles.metaLabel}>
                    <AccessTimeOutlinedIcon css={pageStyles.metaIcon} />
                    Campaign Ends
                  </span>
                  <span css={pageStyles.metaValue}>{formatDateTime(endDate) || "Open"}</span>
                </div>
              </div>
              {!isRedeemed && (originalPrice || discountedPrice) && (
                <div css={[pageStyles.pricePanel, isRedeemed && pageStyles.bookedPricePanel]}>
                  <div css={[pageStyles.priceBlock, isRedeemed && pageStyles.bookedPriceBlock]}>
                    <span css={pageStyles.priceLabel}>Original Price</span>
                    <span css={pageStyles.originalPrice}>{originalPrice || "-"}</span>
                    {!isRedeemed && <span css={pageStyles.priceHint}>Regular price</span>}
                  </div>
                  <div
                    css={[
                      pageStyles.priceBlock,
                      !isRedeemed && pageStyles.offerPriceBlock,
                      isRedeemed && pageStyles.bookedPriceBlock,
                    ]}
                  >
                    {!isRedeemed && <span css={pageStyles.offerRibbon}>Limited Offer</span>}
                    <span css={pageStyles.offerLabel}>Offer Price</span>
                    <span css={[pageStyles.priceValue, isRedeemed && pageStyles.bookedPriceValue]}>
                      {discountedPrice || originalPrice}
                    </span>
                    {!isRedeemed && savings && (
                      <span css={pageStyles.discountPill}>
                        <CheckCircleIcon css={pageStyles.discountIcon} />
                        {discountPercent}% off
                      </span>
                    )}
                    {!isRedeemed && <span css={pageStyles.priceHint}>Limited time only</span>}
                  </div>
                  {savings && (
                    <div css={[pageStyles.priceBlock, isRedeemed && pageStyles.bookedPriceBlock]}>
                      <span css={pageStyles.offerLabel}>You Save</span>
                      <span css={[pageStyles.savingsValue, isRedeemed && pageStyles.bookedSavingsValue]}>
                        {savings}
                      </span>
                      {!isRedeemed && <span css={pageStyles.discountPill}>
                        <CheckCircleIcon css={pageStyles.discountIcon} />
                        Great savings!
                      </span>}
                    </div>
                  )}
                </div>
              )}
              {!isRedeemed && campaign.updatedAt && (
                <p css={pageStyles.detailFootnote}>
                  Last updated {formatDateTime(campaign.updatedAt)}
                </p>
              )}
              {isRedeemed && (
                <div css={pageStyles.bookedFooterLinks}>
                  <button
                    type="button"
                    css={pageStyles.bookedFooterLink}
                    onClick={() => navigate("/my-bookings")}
                  >
                    My Bookings
                  </button>
                  <span css={pageStyles.bookedFooterSeparator} />
                  <button
                    type="button"
                    css={pageStyles.bookedFooterLink}
                    onClick={() => navigate("/campaigns", { state: { selCity } })}
                  >
                    All Campaigns
                  </button>
                </div>
              )}
            </section>

            {!isRedeemed && <aside css={[pageStyles.card, pageStyles.sideCard]}>
              <ConfirmationNumberOutlinedIcon sx={{ color: t.green.solid, fontSize: 26 }} />
              {isExpired && (
                <Alert severity="warning">
                  Your payment window closed and the ticket was released. Book
                  again if seats remain.
                </Alert>
              )}
              {stripeError && <Alert severity="error">{stripeError}</Alert>}
              {!isReserved && !isRedeemed && isNotPublished && (
                <Alert severity="info" css={pageStyles.actionNotice}>
                  This campaign isn&apos;t open for booking yet. Please check back
                  later.
                </Alert>
              )}
              {!isReserved && !isRedeemed && !isNotPublished && isOutsideProfileCity && (
                <Alert severity="info" css={pageStyles.actionNotice}>
                  {outsideCityMessage}
                </Alert>
              )}
              {!isReserved && !isRedeemed && (
                <Button
                  css={pageStyles.actionButton}
                  variant="contained"
                  onClick={onReserve}
                  disabled={
                    reserveMutation.isPending ||
                    retryMutation.isPending ||
                    isOutsideProfileCity ||
                    isNotPublished
                  }
                  title={
                    isNotPublished
                      ? "This campaign isn't open for booking yet."
                      : isOutsideProfileCity
                        ? outsideCityMessage
                        : undefined
                  }
                  startIcon={<ConfirmationNumberOutlinedIcon />}
                >
                  {reserveMutation.isPending || retryMutation.isPending
                    ? "Starting..."
                    : "Book Ticket"}
                </Button>
              )}
              {isReserved && (
                <Button
                  css={pageStyles.actionButton}
                  variant="contained"
                  onClick={hasPaymentSession ? reopenPayment : onRetryPayment}
                  disabled={retryMutation.isPending}
                  startIcon={<PaymentIcon />}
                >
                  {retryMutation.isPending ? "Starting..." : "Complete Payment"}
                </Button>
              )}
              <Button
                css={pageStyles.actionButton}
                variant="outlined"
                onClick={() => navigate("/my-bookings")}
              >
                My Bookings
              </Button>
            </aside>}
              </div>
              <aside css={pageStyles.topCampaignsSidebar} aria-label="More top campaigns">
                <div css={pageStyles.sidebarHeader}>
                  <p css={pageStyles.sidebarEyebrow}>Top Campaigns</p>
                  <h2 css={pageStyles.sidebarTitle}>Limited seats</h2>
                </div>
                <div css={pageStyles.sidebarCampaignList}>
                  {isTopCampaignsLoading && (
                    <>
                      <div css={pageStyles.sidebarCampaignSkeleton} />
                      <div css={pageStyles.sidebarCampaignSkeleton} />
                      <div css={pageStyles.sidebarCampaignSkeleton} />
                    </>
                  )}
                  {!isTopCampaignsLoading &&
                    topCampaigns.map((item) => (
                      <CampaignCard
                        key={getCampaignUuid(item)}
                        campaign={item}
                        compact
                        onOpen={(uuid) =>
                          navigate(`/campaigns/${uuid}`, { state: { selCity } })
                        }
                      />
                    ))}
                  {!isTopCampaignsLoading && topCampaigns.length === 0 && (
                    <div css={pageStyles.sidebarEmpty}>No other campaigns available.</div>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </main>

      <AppModal
        open={isPaymentOpen && hasPaymentSession}
        onClose={closePayment}
        maxWidth="460px"
        showScrollTop={false}
      >
        {hasPaymentSession && (
          <Elements
            key={paymentClientSecret}
            stripe={stripePromise}
            options={{
              clientSecret: paymentClientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#2e7d32",
                  borderRadius: "10px",
                  fontFamily: t.font.family,
                },
              },
            }}
          >
            <CampaignPaymentForm
              onSuccess={onPaymentSuccess}
              amount={paymentAmount}
              campaignTitle={getCampaignTitle(campaign)}
              onProcessingChange={setIsPaymentProcessing}
              mode={paymentMode}
            />
          </Elements>
        )}
      </AppModal>

      <Footer1 />
    </div>
  );
};

export default CampaignDetail;
